import { useStudioLocale } from '@/i18n/use-studio-locale'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Chip } from '@/components/ui/chip'
import { useKg } from '@/features/design-system/kg-provider'
import { useStudioStore } from '@/store/studio-store'
import { isReadableReport, selectViolations, useAskCogoHandler, type ViolationLite } from '@/features/validation-bridge'
import { ValidatorStatsCards } from './validator-stats-cards'
import { ValidatorChecksTable } from './validator-checks-table'
import { ValidatorAlert } from './validator-alert'
import { ValidatorViolationsList } from './validator-violations-list'
import { ValidatorEmptyState } from './validator-empty-state'
import { RunControls } from './run-controls'
import { DesignQualityEvidencePanel } from '@/features/validation/components/design-quality-evidence-panel'
import { readDesignQualityEvidenceModel } from '@/features/validation/design-quality-panel-model'

const DEBOUNCE_MS = 1000

export function ValidatorPanel() {
  const { repo, ready, flagEnabled } = useKg()
  const report = useStudioStore((s) => s.validationReport)
  const running = useStudioStore((s) => s.validatorRunning)
  const error = useStudioStore((s) => s.validatorError)
  const lastRunAt = useStudioStore((s) => s.validatorLastRunAt)
  const runValidation = useStudioStore((s) => s.runValidation)
  const project = useStudioStore((s) => s.currentProject)
  // Engineering detail (SHACL chip, raw rule ids) is developer-persona-only
  // (F15) — creators get task-first language via humanizeRuleId.
  const developer = useStudioStore((s) => s.studioPersona === 'developer')
  const blocks = project?.blocks

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const hasBlocks = blocks && Object.keys(blocks).length > 0

  // Staleness marking lives in a studio-store subscription (project content
  // mutations mark 'kg-shacl' stale regardless of which surface is mounted);
  // this panel only owns the debounced re-run while it is open.
  useEffect(() => {
    if (!flagEnabled || !ready || !repo || !hasBlocks) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      runValidation(repo)
    }, DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [blocks, repo, flagEnabled, ready, hasBlocks, runValidation])

  const violations = useMemo(() => selectViolations(report), [report])
  const designQualityModel = useMemo(() => readDesignQualityEvidenceModel(project), [project])

  const askCogo = useAskCogoHandler()
  const handleAskCogo = useCallback(
    (v: ViolationLite) => {
      askCogo({
        kind: 'validation-issue',
        blockId: v.blockId,
        elementId: v.elementId,
        elementType: v.elementType,
        ruleId: v.ruleId,
        message: v.message,
      })
    },
    [askCogo],
  )

  // selectViolations sorts blocking-first, so a blocking head means the app
  // has at least one hard failure worth headlining.
  const firstBlocking = violations[0]?.severity === 'violation' ? violations[0] : null
  const blockingCount = violations.filter((v) => v.severity === 'violation').length

  const handleRerun = () => {
    if (!repo) return
    runValidation(repo)
  }

  if (!flagEnabled) {
    return (
      <div className="p-7">
        <Header developer={developer} />
        <ValidatorEmptyState reason="flag-off" developer={developer} />
      </div>
    )
  }
  if (!ready) {
    return (
      <div className="p-7">
        <Header developer={developer} />
        <ValidatorEmptyState reason="repo-init" developer={developer} />
      </div>
    )
  }
  if (!hasBlocks) {
    return (
      <div className="p-7">
        <Header developer={developer} running={running} lastRunAt={lastRunAt} onRerun={handleRerun} disabled={true} />
        <ValidatorEmptyState reason="no-blocks" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-7">
        <Header developer={developer} running={running} lastRunAt={lastRunAt} onRerun={handleRerun} />
        <ValidatorEmptyState reason="error" error={error} />
      </div>
    )
  }
  // Task 7 (slice 1) quality-review Minor — this panel's own
  // `selectViolations(report)` call above now silently returns `[]` for a
  // report `isReadableReport` refuses (the crash-fix in
  // select-top-violations.ts), which is indistinguishable from a genuine
  // "0 violations, checks passed" result to everything downstream
  // (ValidatorStatsCards, the alert, the checks table). Disclose it
  // instead — the same discipline `use-dispatch-validation-bridge.ts`'s
  // 'unreadable' summary already applies on the chat surface.
  if (report && !isReadableReport(report)) {
    return (
      <div className="p-7">
        <Header developer={developer} running={running} lastRunAt={lastRunAt} onRerun={handleRerun} />
        <ValidatorEmptyState reason="unreadable" developer={developer} />
      </div>
    )
  }
  if (!report && !running) {
    return (
      <div className="p-7">
        <Header developer={developer} running={running} lastRunAt={lastRunAt} onRerun={handleRerun} />
        <ValidatorEmptyState reason="idle" />
      </div>
    )
  }

  return (
    <div className="overflow-auto p-7">
      <Header developer={developer} running={running} lastRunAt={lastRunAt} onRerun={handleRerun} />
      <DesignQualityEvidencePanel model={designQualityModel} />
      <ValidatorStatsCards report={report} running={running} />
      <ValidatorAlert violation={firstBlocking} blockingCount={blockingCount} onAskCogo={handleAskCogo} showTechnicalDetail={developer} />
      <ValidatorViolationsList violations={violations} onAskCogo={handleAskCogo} showTechnicalDetail={developer} />
      <ValidatorChecksTable rows={report?.rows ?? []} showRuleIds={developer} />
    </div>
  )
}

interface HeaderProps {
  running?: boolean
  lastRunAt?: number | null
  onRerun?: () => void
  disabled?: boolean
  /** Developer persona: show the engine chip (SHACL gates) — jargon for creators. */
  developer?: boolean
}

function Header({ running, lastRunAt, onRerun, disabled, developer }: HeaderProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.validatorPanel
  return (
    <div className="mb-5 flex items-start justify-between">
      <div>
        <Chip variant="primary" className="mb-2">
          {copy.specChip}
        </Chip>
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-xl font-semibold">{copy.heading}</h2>
          {developer && <Chip>SHACL · structural+binding</Chip>}
        </div>
      </div>
      {onRerun && (
        <RunControls
          running={running ?? false}
          lastRunAt={lastRunAt ?? null}
          onRerun={onRerun}
          disabled={disabled}
        />
      )}
    </div>
  )
}
